export interface StockfishSearchRequest {
  readonly searchId: number;
  readonly gameEpoch: number;
  readonly positionRevision: number;
  readonly fen: string;
  readonly nodes?: number;
  readonly depth?: number;
  /** Fixed UCI thinking budget for this move. */
  readonly moveTimeMs?: number;
  readonly multiPv?: number;
}

export type StockfishScore =
  | {
      readonly kind: 'cp';
      readonly value: number;
      readonly bound?: 'lower' | 'upper';
    }
  | {
      readonly kind: 'mate';
      readonly value: number;
      readonly bound?: 'lower' | 'upper';
    };

export interface StockfishPrincipalVariation {
  readonly multiPv: number;
  readonly depth: number | null;
  readonly nodes: number | null;
  readonly score: StockfishScore | null;
  readonly moves: readonly string[];
}

export interface StockfishBestMoveResult extends StockfishSearchRequest {
  readonly status: 'bestmove';
  readonly bestMove: string | null;
  readonly ponder: string | null;
  readonly principalVariations: readonly StockfishPrincipalVariation[];
}

export interface StockfishCancelledResult extends StockfishSearchRequest {
  readonly status: 'cancelled';
  readonly reason: 'cancelled' | 'superseded' | 'destroyed';
}

export type StockfishSearchOutcome = StockfishBestMoveResult | StockfishCancelledResult;

export interface StockfishWorkerMessageEvent {
  readonly data: unknown;
}

export interface StockfishWorkerErrorEvent {
  readonly message?: string;
  readonly error?: unknown;
}

/** Minimal classic Worker surface used by the CapyChess UCI adapter. */
export interface StockfishWorkerLike {
  onmessage: ((event: StockfishWorkerMessageEvent) => void) | null;
  onerror: ((event: StockfishWorkerErrorEvent) => void) | null;
  postMessage(message: string): void;
  terminate(): void;
}

export type StockfishWorkerFactory = () => StockfishWorkerLike;

export interface ValidateStockfishBestMoveInput {
  readonly request: StockfishSearchRequest;
  readonly bestMove: string | null;
}

/**
 * Validates a candidate against the caller's rules authority. The adapter never
 * applies the move itself.
 */
export type StockfishBestMoveValidator = (
  input: ValidateStockfishBestMoveInput,
) => boolean;

export interface StockfishClientOptions {
  readonly createWorker: StockfishWorkerFactory;
  readonly validateBestMove: StockfishBestMoveValidator;
  readonly hashMb?: number;
  readonly handshakeTimeoutMs?: number;
  readonly searchTimeoutMs?: number;
  readonly stopTimeoutMs?: number;
}

export type StockfishClientErrorCode =
  | 'destroyed'
  | 'illegal-bestmove'
  | 'invalid-request'
  | 'malformed-output'
  | 'timeout'
  | 'worker-error';

export class StockfishClientError extends Error {
  public constructor(
    public readonly code: StockfishClientErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'StockfishClientError';
  }
}
